import React, { useState, useEffect, useContext } from "react";
import {
  ArrowRight,
  PlayCircle,
  CheckCircle2,
  ChevronDown,
  Star,
  Users,
  Clock,
  Download,
  Languages,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MainContext } from "@/context/MainContext";
import { baseURL, cdnPath } from "@/utility/api";
import axios from "axios";

const GradientButton = ({
  children,
  onClick,
  className,
  variant = "primary",
}) => (
  <button
    onClick={onClick}
    className={`px-6 py-3 rounded-full font-medium text-sm relative group overflow-hidden ${
      variant === "primary"
        ? "bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
        : "bg-white/10 hover:bg-white/20 text-white"
    } ${className}`}
  >
    <span className="relative z-10 flex items-center gap-2">{children}</span>
    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-violet-600/40 to-indigo-600/40 opacity-0 group-hover:opacity-100 transition-opacity blur-xl"></div>
  </button>
);

const Card = ({ children, className }) => (
  <div
    className={`bg-gray-900/50 backdrop-blur-xl border border-gray-800/60 rounded-xl hover:border-violet-500/30 transition-all duration-300 ${className}`}
  >
    {children}
  </div>
);

const NewCourseDescV2 = () => {
  const [activeModule, setActiveModule] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [course, setCourse] = useState(null);
  const { id } = useParams();
  // const id = 101;
  const { user } = useContext(MainContext);
  const [hasPurchased, setHasPurchased] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isFirstTime, setIsFirstTime] = useState(true);
  useEffect(() => {
    setIsVisible(true);
    async function fetchData() {
      setIsLoading(true);
      const response = await axios.get(`${baseURL}/courses/get-course/${id}`, {
        withCredentials: true,
      });
      setCourse(response.data);
      if (user) {
        const response2 = await axios.get(
          `${baseURL}/courses/has-purchased/${id}`,
          { withCredentials: true }
        );
        setHasPurchased(response2.data.hasPurchased);
        setIsFirstTime(response2.data.isFirstTime);
      }
      setIsLoading(false);
    }
    fetchData();
  }, [user]);

  const handleNavigation = () => {
    if (isFirstTime) {
      const currentPath = location.pathname; // Get the current URL path
      navigate(`${currentPath}/pay`);
    } else {
      navigate(`/my-courses`);
    }
    // Append `/pay` to the current URL
  };

  const features = [
    { icon: Clock, text: "10 days validity" },
    { icon: Download, text: "Downloadable Resources" },
    { icon: Languages, text: "Hindi" },
    // { icon: Users, text: "Community Support" },
  ];

  const stats = [
    { value: "100+", label: "Students Enrolled" },
    { value: "4.7", label: "Average Rating" },
    { value: "2+", label: "Hours of Content" },
  ];

  const modules = [
    {
      id: 1,
      title: "The Introduction",
      description:
        "Understand what you’ll learn and how each module will help you land a role confidently.",
      lessons: [
        "What this course will cover",
        "Strategy overview from roles to resumes",
        "Your learning journey",
      ],
    },
    {
      id: 2,
      title: "Understanding Brands & Roles",
      description:
        "Dive into the job market: from dream brands to realistic first jobs.",
      lessons: [
        "Roles to target as a fresher",
        "Brands you should aim for (and avoid)",
        "Easy vs Tough job roles",
      ],
    },
    {
      id: 3,
      title: "Cold Mailing & LinkedIn Strategy",
      description:
        "Real outreach strategies that worked for me — and still do.",
      lessons: [
        "High-conversion cold emails",
        "LinkedIn reachouts that get replies",
        "Follow-up gameplan",
      ],
    },
    {
      id: 4,
      title: "How to Find Jobs",
      description: "Where the real openings are — and how to stand out.",
      lessons: [
        "Job boards and platforms that work",
        "How to ask for referrals",
        "Spotting and tracking good opportunities",
      ],
    },
    {
      id: 5,
      title: "Resume That Gets Shortlisted",
      description: "Make your resume work for you — not the ATS.",
      lessons: [
        "Format, keywords, and flow",
        "Common fresher resume mistakes",
        "Template walkthrough",
      ],
    },
    {
      id: 6,
      title: "Interview Preparation",
      description: "No cramming — just smart prep for real-life interviews.",
      lessons: [
        "How to prepare effectively",
        "Common questions and frameworks",
        "What companies actually look for",
      ],
    },
    {
      id: 7,
      title: "FAQs and Red Flags",
      description: "Stuff no one tells you — but you must know.",
      lessons: [
        "Contractual vs full-time roles",
        "What are ghost job postings?",
        "Gaps, mistakes & how to handle them",
      ],
    },
  ];

  return (
    <div className="min-h-screen md:-mt-16 bg-gray-950 text-white">
      {/* Animated Background Pattern */}
      <div className="fixed inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.8)_100%)]">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSg5OSwgMTAyLCAyNDEsIDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
      </div>

      {/* Hero Section */}
      <div className="pt-24 lg:pt-40 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div
              className={`space-y-8 transition-all duration-1000 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight">
                Master the Art of{" "}
                <span className="relative">
                  <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">
                    Cracking Off-Campus Placements
                  </span>
                </span>
              </h1>

              <p className="text-gray-400 text-lg sm:text-xl leading-relaxed">
                Learn job hunting the real way — from someone who's done it all
                off-campus. Whether it's choosing the right roles, reaching out
                on LinkedIn, building an ATS-proof resume, or prepping for
                interviews, this course gives you exactly what works. No fluff.
                Just honest strategies.
              </p>

              {/* NEW CONTENT ADDED HERE */}
              <div className="flex flex-wrap items-center gap-3 !mt-4">
                <div className="flex items-center gap-2 bg-gradient-to-r from-violet-500/10 to-indigo-500/10 backdrop-blur-sm border border-violet-500/20 rounded-full px-4 py-2 text-sm text-gray-300 hover:border-violet-500/40 transition-all duration-300">
                  <span>🎯</span>
                  <span>Built from my own journey</span>
                </div>
                <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10 backdrop-blur-sm border border-orange-500/20 rounded-full px-4 py-2 text-sm text-gray-300 hover:border-orange-500/40 transition-all duration-300">
                  <span>💼</span>
                  <span>Final-year & graduate friendly</span>
                </div>
              </div>
              {/* END NEW CONTENT */}

              <div className="flex flex-wrap items-center gap-6">
                <GradientButton
                  className="group"
                  onClick={
                    hasPurchased
                      ? () => {
                          navigate(`/view-courses/${id}`);
                        }
                      : handleNavigation
                  }
                >
                  {!hasPurchased ? (
                    <>
                      {" "}
                      {isFirstTime ? "Enroll now" : "Extend"} <br></br>
                    </>
                  ) : (
                    "View Course"
                  )}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </GradientButton>

                <GradientButton
                  className="group"
                  onClick={() => {
                    navigate(`/courses/102/pay?isGift=true`);
                  }}
                >
                  Gift this course
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </GradientButton>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {stats.map((stat, index) => (
                  <Card key={index} className="p-4 text-center group">
                    <div className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent group-hover:scale-110 transition-transform">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </Card>
                ))}
              </div>
            </div>

            <div
              className={`relative group transition-all duration-1000 delay-300 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-indigo-600/20 blur-3xl transition-all duration-500 group-hover:blur-4xl"></div>
              <Card className="relative overflow-hidden transform group-hover:scale-[1.02] transition-all duration-500">
                <div className="absolute top-2 right-2 flex items-center gap-1 bg-gray-950/80 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-gray-200">
                    {stats[1]?.value || 4.7}
                  </span>
                </div>

                <img
                  src={`${cdnPath}/PlacementCourse.jpg`}
                  alt="Course Preview"
                  className="w-full  object-cover"
                />

                <div className="p-6 space-y-6">
                  <div className="flex flex-wrap gap-4">
                    {features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-sm text-gray-300"
                      >
                        <feature.icon className="w-4 h-4 text-violet-400" />
                        <span>{feature.text}</span>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">
                      What's Included:
                    </h3>
                    <div className="grid gap-3">
                      {course?.main_features.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 text-gray-300 group/item"
                        >
                          <div className="w-5 h-5 rounded-full bg-violet-400/10 flex items-center justify-center flex-shrink-0 group-hover/item:bg-violet-400/20 transition-colors">
                            <CheckCircle2 className="w-3 h-3 text-violet-400" />
                          </div>
                          <span className="group-hover/item:text-white transition-colors">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-800">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="text-gray-400 text-sm line-through">
                          ₹{course?.price}
                        </div>
                        <div className="text-2xl font-bold text-white">
                          ₹{course?.discounted_price}
                        </div>
                      </div>
                      <GradientButton
                        onClick={
                          hasPurchased
                            ? () => {
                                navigate(`/view-courses/${id}`);
                              }
                            : handleNavigation
                        }
                      >
                        {!hasPurchased ? (
                          <>{isFirstTime ? "Get Started" : "Extend"} </>
                        ) : (
                          "View Course"
                        )}
                        <ArrowRight className="w-4 h-4" />
                      </GradientButton>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Curriculum Section */}
      <div className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-900/50 to-gray-950"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Course Curriculum
            </h2>
            <p className="text-gray-400 text-lg">
              Master every stage of landing your off-campus full-time job
            </p>
          </div>

          <div className="space-y-6">
            {modules.map((module) => (
              <Card key={module.id}>
                <button
                  onClick={() =>
                    setActiveModule(
                      activeModule === module.id ? null : module.id
                    )
                  }
                  className="w-full p-6 flex items-center justify-between text-left hover:bg-gray-800/20 transition-colors"
                >
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-violet-400/10 flex items-center justify-center text-violet-400 text-sm font-bold">
                        {module.id}
                      </span>
                      {module.title}
                    </h3>
                    <p className="text-gray-400">{module.description}</p>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                      activeModule === module.id ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out ${
                    activeModule === module.id
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0"
                  } overflow-hidden`}
                >
                  <div className="p-6 pt-0 space-y-4">
                    <ul className="space-y-2">
                      {module.lessons.map((lesson, index) => (
                        <li
                          key={index}
                          className="flex items-center gap-3 text-gray-300"
                        >
                          <CheckCircle2 className="w-4 h-4 text-violet-400" />
                          {lesson}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
      <div className="py-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-900/50 to-gray-950"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Resources Included
            </h2>
          </div>

          <div className="space-y-6">
            {course?.resources.map((module, index) => (
              <Card key={index}>
                <div className="w-full p-6 flex items-center justify-between text-left hover:bg-gray-800/20 transition-colors">
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-violet-400/10 flex items-center justify-center text-violet-400 text-sm font-bold">
                        {index + 1}
                      </span>
                      {module}
                    </h3>
                    {/* <p className="text-gray-400">{module.description}</p> */}
                  </div>
                </div>

                {/* <div
                  className={`transition-all duration-300 ease-in-out ${
                    activeModule === module.id
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0"
                  } overflow-hidden`}
                >
                  <div className="p-6 pt-0 space-y-4">
                    <ul className="space-y-2">
                      {module.lessons.map((lesson, index) => (
                        <li key={index} className="flex items-center gap-3 text-gray-300">
                          <CheckCircle2 className="w-4 h-4 text-violet-400" />
                          {lesson}
                        </li>
                      ))}
                    </ul>
                  </div> */}
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewCourseDescV2;
